import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import type { ApiError } from '@/api/errors'
import { Page, PageHeader } from '@/components/Page'
import { Badge } from '@/components/Badge'
import { Banner } from '@/components/Banner'
import { RecordTable, type RecordColumn } from '@/components/RecordTable'
import { Card } from '@/components/Card'
import { EmptyState } from '@/components/EmptyState'
import { Icon } from '@/components/Icon'
import { SegmentedControl } from '@/components/SegmentedControl'
import { Spinner } from '@/components/Spinner'
import { formatIdentityLine } from '@/lib/age'
import { formatDaysLate, formatVisitDate, todayIso } from '@/lib/dates'
import { Button } from '@/components/Button'
import { useToast } from '@/components/Toast'
import { useModals } from '@/app/modals/ModalContext'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { formatClock } from '@/lib/times'
import { appointments as appointmentsApi } from '@/features/appointments/api'
import type { AppointmentResponse, Paginated } from '@/api/types'

/** How far ahead each window looks when listing booked appointments. */
const WINDOW_DAYS: Record<FollowUpWindow, number> = {
  overdue: 0,
  today: 0,
  week: 7,
  all: 365,
}

function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number)
  const date = new Date(y ?? 1970, (m ?? 1) - 1, (d ?? 1) + days)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}
import { insights, type FollowUp, type FollowUpWindow } from './api'
import { errorMessage } from '@/api/messages'

const WINDOWS: { value: FollowUpWindow; label: string }[] = [
  { value: 'overdue', label: 'Overdue' },
  { value: 'today', label: 'Today' },
  { value: 'week', label: 'Next 7 days' },
  { value: 'all', label: 'All' },
]

const VIEWS: { value: View; label: string }[] = [
  { value: 'booked', label: 'Booked' },
  { value: 'due', label: 'Due back' },
]

type View = 'booked' | 'due'

/**
 * The two lists, as columns rather than as markup.
 *
 * `RecordTable` renders these as a table on a desktop and as a card per record
 * on a phone, so "Due" cannot be a column the phone quietly loses. A cell that
 * returns `null` is a fact this record does not have: the table prints an em
 * dash to keep the grid aligned, the card omits the line.
 */
const DUE_COLUMNS: RecordColumn<FollowUp>[] = [
  {
    key: 'patient',
    header: 'Patient',
    primary: true,
    className: 'font-semibold text-fg',
    cell: (row) => (
      <>
        <Link to={`/patients/${row.patient_id}`}>{row.full_name}</Link>
        <div className="text-sm font-normal text-fg-subtle">
          {formatIdentityLine(row.age, row.sex)}
        </div>
      </>
    ),
  },
  {
    key: 'op',
    header: 'OP number',
    className: 'text-fg-subtle',
    cell: (row) => row.op_number,
  },
  {
    key: 'due',
    header: 'Due',
    className: 'whitespace-nowrap',
    cell: (row) => formatVisitDate(row.due_on),
  },
  {
    key: 'seen',
    header: 'Last seen',
    className: 'whitespace-nowrap text-fg-subtle',
    cell: (row) => formatVisitDate(row.last_seen_on),
  },
  {
    key: 'status',
    header: 'Status',
    // Words, not a colour: "14 days late" is unambiguous to anyone, including
    // a colour-blind reader.
    cell: (row) =>
      row.days_overdue > 0 ? (
        <Badge tone="warning">{formatDaysLate(row.days_overdue)}</Badge>
      ) : (
        <Badge tone="success">Upcoming</Badge>
      ),
  },
]

function bookedColumns(
  onCancel: (row: AppointmentResponse) => void,
): RecordColumn<AppointmentResponse>[] {
  return [
    {
      key: 'patient',
      header: 'Patient',
      primary: true,
      className: 'font-semibold text-fg',
      cell: (row) => (
        <>
          <Link to={`/patients/${row.patient_id}`}>{row.patient_name}</Link>
          {row.patient_phone && (
            <div className="text-sm font-normal text-fg-subtle">{row.patient_phone}</div>
          )}
        </>
      ),
    },
    {
      key: 'op',
      header: 'OP number',
      className: 'text-fg-subtle',
      cell: (row) => row.op_number,
    },
    {
      key: 'when',
      header: 'When',
      className: 'whitespace-nowrap',
      cell: (row) => (
        <>
          {formatVisitDate(row.scheduled_on)}
          {row.scheduled_at && (
            <div className="text-fg-subtle tabular-nums">{formatClock(row.scheduled_at)}</div>
          )}
        </>
      ),
    },
    {
      key: 'where',
      header: 'Where',
      className: 'text-fg-subtle',
      cell: (row) => row.location,
    },
    {
      key: 'reason',
      header: 'Reason',
      className: 'text-fg-subtle',
      cell: (row) => row.reason,
    },
    {
      key: 'actions',
      header: 'Actions',
      action: true,
      // Outlined in red, not a ghost: it takes a booking away, and a
      // destructive action that looks like plain text is one somebody clicks
      // by accident.
      cell: (row) => (
        <Button
          variant="destructiveOutline"
          size="small"
          aria-label={`Cancel the appointment for ${row.patient_name}`}
          onClick={() => onCancel(row)}
        >
          <Icon name="close" size="1rem" />
          Cancel
        </Button>
      ),
    },
  ]
}

/**
 * Two lists that answer two different questions.
 *
 * "Booked" is the appointment book — who the clinic has promised a date to.
 * "Due back" is recall: the next-visit date a doctor wrote at the end of a
 * consultation, which is a patient who *should* return, not one who is expected
 * on any particular day.
 */
export function AppointmentsScreen() {
  const [params, setParams] = useSearchParams()
  const modals = useModals()
  // `on` comes from clicking a booking in the calendar: it pins the list to
  // that one day rather than a window, so the appointment that was clicked is
  // on screen with whoever else is in that day.
  const on = params.get('on')
  const view = (params.get('view') as View | null) ?? 'booked'
  const window = (params.get('window') as FollowUpWindow | null) ?? 'week'

  const from = on ?? todayIso()
  const to = on ?? addDays(from, WINDOW_DAYS[window])

  const booked = useQuery<Paginated<AppointmentResponse>, ApiError>({
    queryKey: ['appointments', from, to],
    queryFn: ({ signal }) => appointmentsApi.list(from, to, signal),
    enabled: view === 'booked',
  })

  const list = useQuery<{ data: FollowUp[]; total: number }, ApiError>({
    queryKey: ['followups', window],
    queryFn: ({ signal }) => insights.followups(window, 100, signal),
    enabled: view === 'due',
  })
  const rows = list.data?.data ?? []
  // The server's count, not the rows it returned — this list stops at a limit,
  // and "100 patients" while 140 are overdue is the one number nobody may guess.
  const dueTotal = list.data?.total
  const appointments = booked.data?.data ?? []
  const active = view === 'booked' ? booked : list

  const book = (
    <Button onClick={() => modals.open({ kind: 'appointment' })}>
      <Icon name="plus" />
      Book appointment
    </Button>
  )

  return (
    <Page fill action={book}>
      <PageHeader
        eyebrow="Booking"
        title="Appointments"
        description={
          view === 'due'
            ? 'Patients you asked to come back, from the next-visit date on their consultation.'
            : on
              ? `Booked for ${formatVisitDate(on)}.`
              : 'Who the clinic has booked in, and when.'
        }
        primary={book}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            {on && (
              <Button variant="secondary" onClick={() => setParams({ view, window })}>
                <Icon name="close" size="1rem" />
                Showing one day
              </Button>
            )}
            <SegmentedControl
              label="View"
              options={VIEWS}
              value={view}
              onChange={(next) => setParams({ view: next, window })}
            />
            {!on && (
              <SegmentedControl
                label="Window"
                options={WINDOWS}
                value={window}
                onChange={(next) => setParams({ view, window: next })}
              />
            )}
          </div>
        }
      />

      {active.error && (
        <Banner tone={active.error.isNetwork ? 'warning' : 'danger'}>
          {errorMessage(active.error)}
        </Banner>
      )}

      {view === 'booked' ? (
        <BookedList
          rows={appointments}
          loading={booked.isLoading}
          onBook={() => modals.open({ kind: 'appointment' })}
        />
      ) : (
      <Card
        title={`${dueTotal ?? rows.length} ${(dueTotal ?? rows.length) === 1 ? 'patient' : 'patients'}`}
        flush
        fill
      >
        {list.isLoading ? (
          <Spinner label="Loading follow-ups" />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="calendar" size="2rem" />}
            title="Nobody due in this window"
            body="Set a next-visit date when recording a consultation and the patient appears here."
          />
        ) : (
          <RecordTable
            caption={`${dueTotal ?? rows.length} patients due back`}
            columns={DUE_COLUMNS}
            rows={rows}
            rowKey={(row) => `${row.patient_id}-${row.due_on}`}
          />
        )}
      </Card>
      )}
    </Page>
  )
}

function BookedList({
  rows,
  loading,
  onBook,
}: {
  rows: AppointmentResponse[]
  loading: boolean
  onBook: () => void
}) {
  const queryClient = useQueryClient()
  const toast = useToast()
  // Held rather than cancelled on the spot: somebody was told to come.
  const [pending, setPending] = useState<AppointmentResponse | null>(null)
  const cancel = useMutation({
    mutationFn: (id: string) => appointmentsApi.cancel(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['appointments'] })
      await queryClient.invalidateQueries({ queryKey: ['calendar'] })
      setPending(null)
      toast.show('Appointment cancelled')
    },
    onError: (caught) => toast.showError(errorMessage(caught)),
  })

  return (
    <Card title={`${rows.length} ${rows.length === 1 ? 'appointment' : 'appointments'}`} flush fill>
      {loading ? (
        <Spinner label="Loading appointments" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Icon name="calendar" size="2rem" />}
          title="Nobody booked in this window"
          body="Book a patient in and they appear here and on the calendar."
          action={
            <Button onClick={onBook}>
              <Icon name="plus" />
              Book appointment
            </Button>
          }
        />
      ) : (
        <RecordTable
          caption={`${rows.length} booked appointments`}
          columns={bookedColumns(setPending)}
          rows={rows}
          rowKey={(row) => row.id}
        />
      )}

      <ConfirmDialog
        open={Boolean(pending)}
        title="Cancel this appointment?"
        confirmLabel="Cancel appointment"
        cancelLabel="Keep it"
        destructive
        loading={cancel.isPending}
        onConfirm={() => pending && cancel.mutate(pending.id)}
        onCancel={() => setPending(null)}
      >
        {pending && (
          <p>
            <strong className="font-semibold text-fg">{pending.patient_name}</strong> is booked for{' '}
            <strong className="font-semibold text-fg">
              {formatVisitDate(pending.scheduled_on)}
            </strong>
            {pending.scheduled_at ? ` at ${formatClock(pending.scheduled_at)}` : ''}. They will come
            off the list and off the calendar. Nobody is told for you.
          </p>
        )}
      </ConfirmDialog>
    </Card>
  )
}
